/**
 * Platform feedback: haptics, buzzer sound and screen wake lock.
 * Web uses browser APIs; inside the Android app the Capacitor plugins take over.
 */
import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { useSettings } from '@/features/settings/settingsStore';

const native = () => Capacitor.isNativePlatform();

export type HapticKind = 'tap' | 'success' | 'warning' | 'error' | 'long';

export function haptic(kind: HapticKind = 'tap'): void {
  if (!useSettings.getState().haptics) return;
  try {
    if (native()) {
      if (kind === 'tap') void Haptics.impact({ style: ImpactStyle.Light });
      else if (kind === 'success') void Haptics.impact({ style: ImpactStyle.Medium });
      else void Haptics.vibrate({ duration: kind === 'long' ? 250 : 80 });
      return;
    }
    const ms = { tap: 12, success: 20, warning: 60, error: 90, long: 250 }[kind];
    navigator.vibrate?.(ms);
  } catch {
    /* haptics are optional */
  }
}

let audio: AudioContext | null = null;

/** Call from a user gesture (first tap) so sounds may play later. */
export function unlockAudio(): void {
  try {
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    audio = audio ?? new Ctor();
    if (audio.state === 'suspended') void audio.resume();
  } catch {
    audio = null;
  }
}

/** Original synthesised buzzer (no third-party audio files). */
export function buzzer(): void {
  if (!useSettings.getState().sound || !audio) return;
  try {
    const t0 = audio.currentTime;
    const gain = audio.createGain();
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(0.22, t0 + 0.03);
    gain.gain.setValueAtTime(0.22, t0 + 0.85);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 1);
    gain.connect(audio.destination);
    for (const f of [196, 199.5, 392]) {
      const o = audio.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = f;
      o.connect(gain);
      o.start(t0);
      o.stop(t0 + 1.05);
    }
  } catch {
    /* sound is optional */
  }
}

let webLock: WakeLockSentinel | null = null;

export async function keepScreenOn(on: boolean): Promise<void> {
  try {
    if (native()) {
      await (on ? KeepAwake.keepAwake() : KeepAwake.allowSleep());
      return;
    }
    if (on) {
      if (!webLock && 'wakeLock' in navigator) {
        webLock = await navigator.wakeLock.request('screen');
        webLock.addEventListener('release', () => {
          webLock = null;
        });
      }
    } else if (webLock) {
      await webLock.release();
      webLock = null;
    }
  } catch {
    webLock = null;
  }
}
